import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IoAddCircleOutline, IoPersonOutline } from 'react-icons/io5';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/components/ui/utils';
import { useContactsList } from '../../hooks/useContacts';
import { useAuth } from '../../context/AuthContext';
import { ContactCard } from '../domain/ContactCard';

/**
 * TopBar recipient picker — PEL-822.
 *
 * Reads the signed-in user's contacts, shows the currently selected one,
 * and lets the user switch or add a new recipient. The selection is
 * persisted through AuthContext (localStorage) so it survives refreshes.
 *
 * Empty state: single "Add your first recipient" CTA, no dropdown.
 * Loading state: muted skeleton label, popover disabled.
 */

export function RecipientSelector() {
  const { data: contacts, isLoading } = useContactsList();
  const { selectedContactId, setSelectedContactId } = useAuth();
  const navigate = useNavigate();

  const selected =
    contacts?.find((c) => c.id === selectedContactId) ?? contacts?.[0] ?? null;

  // If we have contacts but no stored selection (or the stored id no longer
  // exists), snap to the first contact so the TopBar always shows a value.
  useEffect(() => {
    if (!contacts || contacts.length === 0) return;
    const stillExists = contacts.some((c) => c.id === selectedContactId);
    if (!stillExists) {
      setSelectedContactId(contacts[0].id);
    }
  }, [contacts, selectedContactId, setSelectedContactId]);

  // Loading — render a muted shell that doesn't jump when data arrives.
  if (isLoading) {
    return (
      <div
        className="inline-flex items-center h-11 px-3 rounded-md bg-[var(--background)] text-[13px] text-[var(--text-muted)] w-full max-w-[280px]"
        aria-live="polite"
      >
        Loading recipient…
      </div>
    );
  }

  // Empty state — no dropdown, just a primary-looking CTA to add one.
  if (!contacts || contacts.length === 0) {
    return (
      <Link
        to="/app/recipients/new"
        className={cn(
          'inline-flex items-center gap-2 h-11 px-3 rounded-md',
          'bg-[var(--cta-light)] text-[var(--cta)] hover:opacity-90',
          'text-[13px] font-semibold w-full max-w-[280px] truncate',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
        )}
      >
        <IoAddCircleOutline size={18} aria-hidden />
        Add your first recipient
      </Link>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 h-11 px-3 rounded-md',
            'bg-[var(--surface)] border border-[var(--border)]',
            'hover:bg-[var(--background)]',
            'text-[13px] w-full max-w-[280px]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
          )}
          aria-label="Select recipient"
        >
          <div
            className="h-7 w-7 rounded-full bg-[var(--brand-light)] text-[var(--brand)] flex items-center justify-center shrink-0"
            aria-hidden
          >
            <IoPersonOutline size={14} />
          </div>
          <div className="flex-1 min-w-0 text-left leading-tight">
            <p className="font-semibold text-[13px] truncate text-[var(--text)]">
              {selected
                ? `${selected.firstName} ${selected.lastName}`.trim() ||
                  'Unnamed recipient'
                : 'Select a recipient'}
            </p>
            <p className="text-[11px] text-[var(--text-muted)] truncate">
              {selected?.facilityName ?? ''}
            </p>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[300px]">
        <div className="max-h-[320px] overflow-y-auto p-2">
          {contacts.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              variant="compact"
              selected={selected?.id === c.id}
              onClick={() => {
                setSelectedContactId(c.id);
                // Close popover by blurring; Radix closes on outside click.
                (document.activeElement as HTMLElement | null)?.blur?.();
              }}
            />
          ))}
        </div>
        <Separator />
        <button
          type="button"
          className={cn(
            'w-full flex items-center gap-2 px-3 py-3 text-left',
            'text-[13px] font-semibold text-[var(--cta)]',
            'hover:bg-[var(--background)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
          )}
          onClick={() => navigate('/app/recipients/new')}
        >
          <IoAddCircleOutline size={18} aria-hidden />
          Add recipient
        </button>
      </PopoverContent>
    </Popover>
  );
}
